import { keyframes } from '@emotion/react';
import styled from '@emotion/styled';
import { CSSProperties, ReactNode, useEffect, useState } from 'react';
import { UDText } from 'ud-ui/components';

interface OverlayMessageProps {
  title: ReactNode;
  subtitle?: ReactNode;
  variant?: 'error' | 'info';
  /** Set to `false` to play the exit animation before unmounting. Defaults to always shown. */
  isOpen?: boolean;
  className?: string;
  style?: CSSProperties;
}

const EXIT_DURATION = 300;

/**
 * Full-screen centered message used across the overlay pages for loading /
 * waiting / error states. Pass `style` (e.g. absolute positioning + a lower
 * `z-index`) to overlay it on top of the canvas without hiding the joystick.
 *
 * When toggled by `isOpen`, the component stays mounted for `EXIT_DURATION`
 * to play the fade/pop-out animation instead of disappearing instantly.
 */
function OverlayMessage({ title, subtitle, variant = 'info', isOpen = true, className, style }: OverlayMessageProps) {
  const [shouldRender, setShouldRender] = useState(isOpen);

  useEffect(() => {
    if (isOpen) {
      setShouldRender(true);
      return;
    }

    const timeout = setTimeout(() => setShouldRender(false), EXIT_DURATION);
    return () => clearTimeout(timeout);
  }, [isOpen]);

  if (!shouldRender) return null;

  const boxColor = variant === 'error' ? 'bg-Red' : 'bg-yellow-300';

  return (
    <Wrapper className={className} style={style} isClosing={!isOpen}>
      <Box className={`center flex flex-col gap-2 rounded-2xl p-4 text-center ${boxColor}`} isClosing={!isOpen}>
        <UDText isNew type={'title-1'}>
          {title}
        </UDText>
        {subtitle && (
          <UDText isNew type={'subtitle-3'}>
            {subtitle}
          </UDText>
        )}
      </Box>
    </Wrapper>
  );
}

export default OverlayMessage;

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const fadeOut = keyframes`
  from {
    opacity: 1;
  }
  to {
    opacity: 0;
  }
`;

const popIn = keyframes`
  from {
    opacity: 0;
    transform: scale(0.9) translateY(12px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
`;

const popOut = keyframes`
  from {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
  to {
    opacity: 0;
    transform: scale(0.9) translateY(12px);
  }
`;

const Wrapper = styled.div<{ isClosing: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100vh;
  animation: ${({ isClosing }) => (isClosing ? fadeOut : fadeIn)} ${EXIT_DURATION}ms ease-in-out;
  animation-fill-mode: forwards;
`;

const Box = styled.div<{ isClosing: boolean }>`
  animation: ${({ isClosing }) => (isClosing ? popOut : popIn)} 0.4s cubic-bezier(0.16, 1, 0.3, 1);
  animation-fill-mode: forwards;
`;
